import { useCallback, useEffect, useState } from 'react'

import { DEMO_SPORTS, getSportConfig, SPORT_ID } from '@/config/sports'
import { setDemoMatchDataSport } from '@/services/jsonDataService'
import { setDemoPayload } from '@/services/matchScoreService'
import { DISPLAY_STATE, GFX_SLOT_ID } from '@/types'
import './DemoController.css'

import type { GfxState, ScoreBugPosition } from '@/services/jsonDataService'
import type { GfxConfig } from '@/types'
import type { FC } from 'react'

interface DemoPayload {
  id: string
  name: string
  file: string
}

const SCOREBUG_POSITIONS: { value: ScoreBugPosition; label: string }[] = [
  { value: 'top', label: 'Top Centre' },
  { value: 'bottom', label: 'Bottom Centre' },
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
]

export interface DemoControllerProps {
  initialSport?: SPORT_ID
  onPayloadChange?: (payloadPath: string) => void
  onSportChange?: (sportId: SPORT_ID) => void
  onDisplayStateChange?: (displayState: DISPLAY_STATE) => void
  onScoreBugPositionChange?: (position: ScoreBugPosition) => void
  gfxConfig?: GfxConfig
  onGfxStateChange?: (gfxState: GfxState) => void
}

const DemoController: FC<DemoControllerProps> = ({
  initialSport = SPORT_ID.VOLLEYBALL,
  onPayloadChange,
  onSportChange,
  onDisplayStateChange,
  onScoreBugPositionChange,
  gfxConfig,
  onGfxStateChange,
}) => {
  const [selectedSport, setSelectedSport] = useState<SPORT_ID>(initialSport)
  const [payloads, setPayloads] = useState<DemoPayload[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [autoAdvance, setAutoAdvance] = useState(false)
  const [currentDisplayState, setCurrentDisplayState] = useState<DISPLAY_STATE>(DISPLAY_STATE.INTRO)
  const [scoreBugPosition, setScoreBugPosition] = useState<ScoreBugPosition>('bottom-left')
  const [gfxState, setGfxState] = useState<GfxState>({})

  const sportConfig = getSportConfig(selectedSport)

  // Set initial sport for match data service on mount
  useEffect(() => {
    setDemoMatchDataSport(initialSport)
  }, [initialSport])

  // Handle display state change
  const handleDisplayStateChange = useCallback(
    (displayState: DISPLAY_STATE) => {
      setCurrentDisplayState(displayState)
      onDisplayStateChange?.(displayState)
    },
    [onDisplayStateChange]
  )

  // Handle scorebug position change
  const handleScoreBugPositionChange = useCallback(
    (position: ScoreBugPosition) => {
      setScoreBugPosition(position)
      onScoreBugPositionChange?.(position)
    },
    [onScoreBugPositionChange]
  )

  // Handle banner visibility toggle
  const handleBannerToggle = useCallback(
    (slotId: number) => {
      setGfxState((prev) => {
        const next = { ...prev, [slotId]: { visible: !prev[slotId]?.visible } }
        onGfxStateChange?.(next)
        return next
      })
    },
    [onGfxStateChange]
  )

  // Handle sport change
  const handleSportChange = useCallback(
    (newSportId: SPORT_ID) => {
      setSelectedSport(newSportId)
      setActiveIndex(0)
      setPayloads([])
      // Update the match data service to use sport-specific data
      setDemoMatchDataSport(newSportId)
      onSportChange?.(newSportId)
    },
    [onSportChange]
  )

  // Load payload manifest for sport.
  // Prefer the directory of `defaultDemoPayload` so demo data can live
  // somewhere other than `/demo/<folder>/` (e.g. GoalSport template's
  // `goal-sport` folder vs soccer's `/demo/soccer/` data path).
  useEffect(() => {
    if (!sportConfig) return

    const payloadDir = sportConfig.defaultDemoPayload?.replace(/\/[^/]*$/, '') ?? `/demo/${sportConfig.folder}`
    const indexPath = `${payloadDir}/index.json`

    fetch(indexPath)
      .then((r) => r.json())
      .then((data: DemoPayload[]) => {
        setPayloads(data)
        setActiveIndex(0)
        // Set initial payload
        if (data.length > 0) {
          setDemoPayload(data[0].file)
          onPayloadChange?.(data[0].file)
        }
      })
      .catch((err) => console.error('Failed to load demo payloads:', err))
  }, [sportConfig, onPayloadChange])

  // Handle payload change
  const handlePayloadChange = useCallback(
    (index: number) => {
      setActiveIndex(index)
      const payload = payloads[index]
      if (payload) {
        setDemoPayload(payload.file)
        onPayloadChange?.(payload.file)
      }
    },
    [payloads, onPayloadChange]
  )

  // Auto-advance every 30 seconds
  useEffect(() => {
    if (!autoAdvance || payloads.length === 0) return

    const interval = setInterval(() => {
      setActiveIndex((i) => {
        const nextIndex = (i + 1) % payloads.length
        const payload = payloads[nextIndex]
        if (payload) {
          setDemoPayload(payload.file)
          onPayloadChange?.(payload.file)
        }
        return nextIndex
      })
    }, 30000)

    return () => clearInterval(interval)
  }, [autoAdvance, payloads, onPayloadChange])

  return (
    <div className="demo-controller">
      <div className="demo-controller-header">Demo Mode</div>

      <div className="demo-controller-row">
        <label className="demo-controller-label-text">Sport</label>
        <select
          value={selectedSport}
          onChange={(e) => handleSportChange(Number(e.target.value) as SPORT_ID)}
          className="demo-controller-select"
        >
          {DEMO_SPORTS.map((sportId) => (
            <option key={sportId} value={sportId}>
              {getSportConfig(sportId)?.name}
            </option>
          ))}
        </select>
      </div>

      <div className="demo-controller-row">
        <label className="demo-controller-label-text">Display State</label>
        <select
          value={currentDisplayState}
          onChange={(e) => handleDisplayStateChange(e.target.value as DISPLAY_STATE)}
          className="demo-controller-select"
        >
          <option value={DISPLAY_STATE.INTRO}>Intro</option>
          <option value={DISPLAY_STATE.SCOREBUG}>Scorebug</option>
          <option value={DISPLAY_STATE.SCOREBAR}>Scorebar</option>
        </select>
      </div>

      {currentDisplayState === DISPLAY_STATE.SCOREBUG && (
        <div className="demo-controller-row">
          <label className="demo-controller-label-text">Bug Position</label>
          <select
            value={scoreBugPosition}
            onChange={(e) => handleScoreBugPositionChange(e.target.value as ScoreBugPosition)}
            className="demo-controller-select"
          >
            {SCOREBUG_POSITIONS.map((pos) => (
              <option key={pos.value} value={pos.value}>
                {pos.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="demo-controller-row">
        <label className="demo-controller-label-text">Scenario</label>
        <select
          value={activeIndex}
          onChange={(e) => handlePayloadChange(Number(e.target.value))}
          className="demo-controller-select"
          disabled={payloads.length === 0}
        >
          {payloads.length === 0 ? (
            <option>Loading...</option>
          ) : (
            payloads.map((p, i) => (
              <option key={p.id} value={i}>
                {p.name}
              </option>
            ))
          )}
        </select>
      </div>

      {gfxConfig?.imageSlots?.some((s) => s.slotID === GFX_SLOT_ID.HORIZONTAL_BANNER) && (
        <div className="demo-controller-row">
          <label className="demo-controller-label">
            <input
              type="checkbox"
              checked={gfxState[GFX_SLOT_ID.HORIZONTAL_BANNER]?.visible ?? false}
              onChange={() => handleBannerToggle(GFX_SLOT_ID.HORIZONTAL_BANNER)}
              className="demo-controller-checkbox"
            />
            Horizontal banner
          </label>
        </div>
      )}

      {gfxConfig?.imageSlots?.some((s) => s.slotID === GFX_SLOT_ID.VERTICAL_BANNER) && (
        <div className="demo-controller-row">
          <label className="demo-controller-label">
            <input
              type="checkbox"
              checked={gfxState[GFX_SLOT_ID.VERTICAL_BANNER]?.visible ?? false}
              onChange={() => handleBannerToggle(GFX_SLOT_ID.VERTICAL_BANNER)}
              className="demo-controller-checkbox"
            />
            Vertical banner
          </label>
        </div>
      )}

      <div className="demo-controller-row">
        <label className="demo-controller-label">
          <input
            type="checkbox"
            checked={autoAdvance}
            onChange={() => setAutoAdvance(!autoAdvance)}
            className="demo-controller-checkbox"
          />
          Auto-advance (30s)
        </label>
      </div>
    </div>
  )
}

export default DemoController
